import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Heart, ShoppingBag, User, Menu, LogOut } from "lucide-react";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "../ui/sheet";
import api, { imgUrl } from "../../lib/api";

export default function Header() {
  const { cartCount, wishlist, setCartOpen } = useCart();
  const { user, logout } = useAuth();
  const { nav, theme } = useTheme();
  const navItems = nav.filter((n) => n.visible);
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [query, setQuery] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    if (query.trim()) navigate(`/shop?q=${encodeURIComponent(query.trim())}`);
  };

  if (!theme.header_visible) return null;

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      {theme.announcement_enabled && (
        <div className="text-center py-2 small-caps" style={{ backgroundColor: theme.announcement_bg, color: theme.announcement_text }} data-testid="announcement-bar" data-eid="announcement-text" data-etype="text">
          {settings?.announcement_link ? (
            <Link to={settings.announcement_link} className="hover:text-gold transition-colors duration-300" data-testid="announcement-link">{settings?.announcement || "Wholesale & Retail · Manufacturer Direct"}</Link>
          ) : (
            settings?.announcement || "Wholesale & Retail · Manufacturer Direct"
          )}
        </div>
      )}
      <div className={`border-b border-black/10 transition-colors duration-300 ${scrolled ? "bg-white/70 backdrop-blur-xl" : ""}`} style={scrolled ? {} : { backgroundColor: theme.header_bg }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16 md:h-20 gap-4">
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <button className="md:hidden p-2 -ml-2" data-testid="mobile-menu-button" aria-label="Menu">
                <Menu strokeWidth={1.5} size={22} />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-[#FAF9F6] w-80 max-w-[85vw] overflow-y-auto">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <nav className="flex flex-col gap-5 mt-10 pb-8">
                {navItems.map((n) => (
                  <Link key={n.url} to={n.url} onClick={closeMenu} className="font-serif text-2xl py-1" data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\\s+/g, "-")}`}>{n.label}</Link>
                ))}
                <div className="border-t border-black/10 pt-5">
                  <p className="small-caps text-muted-foreground mb-3">Categories</p>
                  {categories.map((c) => (
                    <Link key={c.id} to={`/category/${c.slug}`} onClick={closeMenu} className="block py-3 text-sm border-b border-black/5" data-testid={`mobile-cat-${c.slug}`}>{c.name}</Link>
                  ))}
                </div>
              </nav>
            </SheetContent>
          </Sheet>

          <Link to="/" className="flex flex-col items-start leading-none" data-testid="logo-link" data-eid="logo" data-etype="text">
            {settings?.theme?.logo_url ? (
              <img src={imgUrl(settings.theme.logo_url)} alt={settings.business_name || "Humaira Crafts"} className="h-8 md:h-10 object-contain" data-testid="logo-image" />
            ) : (
              <>
                <span className="font-serif text-xl md:text-2xl font-semibold tracking-wide">HUMAIRA <span className="text-gold">CRAFTS</span></span>
                <span className="small-caps text-muted-foreground hidden md:block mt-1" style={{ fontSize: "9px" }}>Est. 2002 · Delhi</span>
              </>
            )}
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {navItems.map((n) => (
              <Link key={n.url} to={n.url} className="small-caps hover:text-gold transition-colors duration-300" data-testid={`nav-${n.label.toLowerCase().replace(/\\s+/g, "-")}`}>
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-4 md:gap-5">
            {theme.show_search && (
            <form onSubmit={submitSearch} className="hidden lg:flex items-center border-b border-black/20 focus-within:border-gold transition-colors duration-300">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search décor..."
                className="bg-transparent text-sm py-1 w-36 focus:w-48 transition-all duration-300 outline-none placeholder:text-muted-foreground"
                data-testid="header-search-input"
              />
              <button type="submit" aria-label="Search" data-testid="header-search-button">
                <Search strokeWidth={1.5} size={18} />
              </button>
            </form>
            )}
            {theme.show_wishlist && (
            <Link to="/wishlist" className="relative" data-testid="wishlist-link" aria-label="Wishlist">
              <Heart strokeWidth={1.5} size={20} />
              {wishlist.length > 0 && <span className="absolute -top-2 -right-2 bg-gold text-[#111] text-[10px] w-4 h-4 rounded-full flex items-center justify-center">{wishlist.length}</span>}
            </Link>
            )}
            {theme.show_cart && (
            <button onClick={() => setCartOpen(true)} className="relative" data-testid="cart-link" aria-label="Open bag">
              <ShoppingBag strokeWidth={1.5} size={20} />
              {cartCount > 0 && <span className="absolute -top-2 -right-2 bg-gold text-[#111] text-[10px] w-4 h-4 rounded-full flex items-center justify-center" data-testid="cart-count">{cartCount}</span>}
            </button>
            )}
            {!theme.show_account ? null : user ? (
              <div className="flex items-center gap-3">
                <Link to={user.role === "admin" ? "/admin" : "/account"} data-testid="account-link" aria-label="Account">
                  <User strokeWidth={1.5} size={20} />
                </Link>
                <button onClick={logout} aria-label="Logout" data-testid="logout-button">
                  <LogOut strokeWidth={1.5} size={18} className="text-muted-foreground hover:text-gold transition-colors duration-300" />
                </button>
              </div>
            ) : (
              <Link to="/login" data-testid="login-link" aria-label="Login">
                <User strokeWidth={1.5} size={20} />
              </Link>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
